import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { initials } from "@/lib/format";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { CommandPalette } from "@/components/CommandPalette";
import { SetupWizard } from "@/components/SetupWizard";
import { FirstLoginTour } from "@/components/FirstLoginTour";
import {
  LayoutDashboard, Users, UserPlus, Shield, KeySquare, MessagesSquare, Network,
  LayoutTemplate, ScrollText, Settings as SettingsIcon, UserCog, LogOut, Building2,
  SlidersHorizontal, Timer, UserCheck, Mailbox, Mail, AppWindow, FileSpreadsheet, ShieldAlert, Eye, AlertTriangle, Menu, FlaskConical, Sparkles, Search, X, Boxes,
} from "lucide-react";

export const NAV = [
  { to: "/", key: "nav.dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
  { to: "/onboard", key: "nav.onboard", icon: UserPlus, testid: "nav-onboard", bold: true, perm: "onboarding" },
  { to: "/employees", key: "nav.employees", icon: Users, testid: "nav-employees" },
  { to: "/org-chart", key: "nav.orgChart", icon: Network, testid: "nav-org-chart" },
  { to: "/guests", key: "nav.guests", icon: UserCheck, testid: "nav-guests" },
  { to: "/shared-mailboxes", key: "nav.shared", icon: Mailbox, testid: "nav-shared" },
  { to: "/groups", key: "nav.groups", icon: Shield, testid: "nav-groups" },
  { to: "/distribution-lists", key: "nav.distribution", icon: Mail, testid: "nav-distribution" },
  { to: "/licenses", key: "nav.licenses", icon: KeySquare, testid: "nav-licenses" },
  { to: "/teams", key: "nav.teams", icon: MessagesSquare, testid: "nav-teams" },
  { to: "/m365-groups", key: "nav.m365Groups", icon: Boxes, testid: "nav-m365-groups", perm: "groups.manage" },
  { to: "/role-templates", key: "nav.roles", icon: LayoutTemplate, testid: "nav-roles" },
  { to: "/applications", key: "nav.applications", icon: AppWindow, testid: "nav-applications" },
  { to: "/companies", key: "nav.companies", icon: Building2, testid: "nav-companies" },
  { to: "/actions", key: "nav.actions", icon: ScrollText, testid: "nav-actions", perm: "workflows.view" },
  { to: "/ai-assistant", key: "nav.ai", icon: Sparkles, testid: "nav-ai", aiGated: true },
  { to: "/jobs", key: "nav.jobs", icon: Timer, testid: "nav-jobs", perm: "workflows.view" },
  { to: "/import-export", key: "nav.importExport", icon: FileSpreadsheet, testid: "nav-import-export", perm: "employees.manage" },
  { to: "/preferences", key: "nav.preferences", icon: SlidersHorizontal, testid: "nav-preferences" },
];
export const ADMIN_NAV = [
  { to: "/admins", key: "nav.admins", icon: UserCog, testid: "nav-admins", adminOnly: true },
  { to: "/settings", key: "nav.settings", icon: SettingsIcon, testid: "nav-settings", perm: "settings.manage" },
];

export const APP_VERSION = process.env.REACT_APP_VERSION || "0.1.0";

export function Layout({ children }) {
  const { user, logout, isAdmin, isSuperAdmin, accessMode, accessMessage, can, demoMode, license, e2eMode, ai, wizard, refreshWizard } = useAuth();
  const navVisible = (n) => (n.aiGated ? !!(ai?.available && ai?.canUse) : (!n.perm || can(n.perm)));
  const { t } = useLang();
  const nav = useNavigate();
  const location = useLocation();
  const [errCount, setErrCount] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardStep, setWizardStep] = useState(null);
  const [saasStatus, setSaasStatus] = useState(null);
  const [exoRoleReady, setExoRoleReady] = useState(null);
  const [certBusy, setCertBusy] = useState(false);
  const regenerateCert = async () => {
    setCertBusy(true);
    try {
      await api.post("/settings/entra/generate-cert");
      const { toast } = await import("sonner");
      toast.success(t("shg.certGenerated"));
      const { data } = await api.get("/auth/sso/status");
      setSaasStatus(data);
    } catch (e) {
      const { toast } = await import("sonner");
      toast.error(apiErr(e));
    } finally { setCertBusy(false); }
  };
  const [tourOpen, setTourOpen] = useState(false);
  const [wizardAutoOpened, setWizardAutoOpened] = useState(false);

  // Close the mobile drawer whenever the route changes.
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Let other pages (e.g. the dashboard onboarding card) open the setup wizard.
  useEffect(() => {
    const open = () => setWizardOpen(true);
    window.addEventListener("open-setup-wizard", open);
    return () => window.removeEventListener("open-setup-wizard", open);
  }, []);

  // Auto-open the Setup Wizard once after login when setup is still needed (SaaS onboarding).
  useEffect(() => {
    if (wizard?.needed && isAdmin && !wizardAutoOpened) {
      setWizardAutoOpened(true);
      setWizardOpen(true);
    }
  }, [wizard, isAdmin, wizardAutoOpened]);

  // SaaS connection status drives the top-bar setup progress ring (and refreshes when the wizard closes).
  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    api.get("/auth/sso/status").then((r) => { if (!cancelled) setSaasStatus(r.data); }).catch(() => {});
    return () => { cancelled = true; };
  }, [isAdmin, wizardOpen, wizard]);

  // Exchange Online is a REQUIRED SaaS setup step; poll the lightweight role-status so the setup ring
  // reflects it and turns complete on its own once the Exchange Administrator role propagates.
  useEffect(() => {
    if (!isAdmin || !(saasStatus?.isSaaS || saasStatus?.exoEnabledFlag)) { setExoRoleReady(null); return; }
    let cancelled = false;
    const check = async () => {
      try {
        const { data } = await api.get("/settings/exo/role-status");
        if (!cancelled) setExoRoleReady(data?.roleReady === true);
        return data?.roleReady === true;
      } catch { return false; }
    };
    let id;
    check().then((ready) => { if (!ready && !cancelled) id = setInterval(async () => { if (await check()) clearInterval(id); }, 10000); });
    return () => { cancelled = true; if (id) clearInterval(id); };
  }, [isAdmin, saasStatus?.isSaaS, saasStatus?.exoEnabledFlag, wizardOpen]);

  // Return-from-admin-consent: reopen the wizard on the Graph step (?setupWizard=graph).
  useEffect(() => {
    const u = new URL(window.location.href);
    if (u.searchParams.get("setupWizard")) {
      const target = u.searchParams.get("setupWizard");
      setWizardStep(target === "graph" ? 1 : 0);
      setWizardOpen(true);
      refreshWizard && refreshWizard();
      const se = u.searchParams.get("ssoError");
      if (se) { import("sonner").then(({ toast }) => toast.error(se)); }
      else if (u.searchParams.get("graphConsent") === "ok") {
        import("sonner").then(({ toast }) => toast.success(t("shg.consentOk")));
      }
      u.searchParams.delete("setupWizard");
      u.searchParams.delete("ssoError");
      u.searchParams.delete("graphConsent");
      window.history.replaceState({}, "", u.toString());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // First-login product tour: highlight the main nav once per admin on a desktop viewport.
  // Hold it back until setup is finished — don't start while the wizard is still needed or open,
  // so a fresh admin completes onboarding first and the tour opens right after.
  useEffect(() => {
    if (!user || !isAdmin) return;
    if (typeof window === "undefined" || window.innerWidth < 768) return;
    const key = `tour_seen_${user.id}`;
    if (localStorage.getItem(key)) return;
    if (wizard?.needed || wizardOpen) return;
    const id = setTimeout(() => setTourOpen(true), 900);
    return () => clearTimeout(id);
  }, [user, isAdmin, wizard, wizardOpen]);

  const closeTour = () => {
    if (user) localStorage.setItem(`tour_seen_${user.id}`, new Date().toISOString());
    setTourOpen(false);
  };

  useEffect(() => {
    if (!isSuperAdmin) return;
    if (!localStorage.getItem("sa_errors_seen")) localStorage.setItem("sa_errors_seen", new Date().toISOString());
    const poll = () => {
      const since = localStorage.getItem("sa_errors_seen") || new Date().toISOString();
      api.get(`/superadmin/logs/errors-count?since=${encodeURIComponent(since)}`).then((r) => setErrCount(r.data.count)).catch(() => {});
    };
    poll();
    const id = setInterval(poll, 30000);
    return () => clearInterval(id);
  }, [isSuperAdmin]);

  useEffect(() => {
    if (isSuperAdmin && location.pathname === "/superadmin") {
      localStorage.setItem("sa_errors_seen", new Date().toISOString());
      setErrCount(0);
    }
  }, [location.pathname, isSuperAdmin]);

  const doLogout = async () => {
    await logout();
    nav("/login");
  };

  const linkCls = ({ isActive }) =>
    `flex items-center gap-3 px-4 py-2.5 text-sm font-medium border-l-2 transition-colors duration-150 ${
      isActive
        ? "border-primary bg-primary/5 text-primary"
        : "border-transparent text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900"
    }`;

  const graceDaysLeft = license?.buildGraceUntil
    ? Math.max(0, Math.ceil((new Date(license.buildGraceUntil + "T23:59:59") - new Date()) / 86400000))
    : null;

  const trialDaysLeft = (() => {
    const days = [];
    if (license?.trial && license.trial.daysLeft != null) days.push(license.trial.daysLeft);
    (license?.addons || []).forEach((a) => {
      if (a.trial && a.valid && a.daysLeft != null) days.push(a.daysLeft);
    });
    return days.length ? Math.max(0, Math.min(...days)) : null;
  })();

  return (
    <div className="flex h-screen overflow-hidden bg-zinc-50">
      <CommandPalette />
      <aside className="hidden w-64 shrink-0 flex-col border-r border-zinc-200 bg-white md:flex">
        <div className="flex h-16 items-center gap-2.5 border-b border-zinc-200 px-5">
          <img src="/logo.svg" alt="Scalio Nexus" className="h-8 w-8 shrink-0" />
          <div className="leading-none">
            <div className="flex items-center gap-1.5">
              <div className="font-display text-sm font-black uppercase tracking-tight text-zinc-950">
                Scalio Nexus
              </div>
              {demoMode && (
                <span data-testid="demo-badge" title={t("demo.bannerTitle")}
                  className="rounded-sm bg-sky-100 px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase tracking-[0.15em] text-sky-700 ring-1 ring-sky-300">
                  Demo
                </span>
              )}
              {!demoMode && license && license.edition === "community" && (
                <span data-testid="edition-badge" title={t("license.communityTip")}
                  className="rounded-sm bg-amber-100 px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase tracking-[0.15em] text-amber-700 ring-1 ring-amber-300">
                  Free
                </span>
              )}
              {!demoMode && license && license.edition === "pro" && (
                <span data-testid="edition-badge" title={license.customer || "Licensed"}
                  className="rounded-sm bg-emerald-100 px-1.5 py-0.5 font-mono text-[8px] font-bold uppercase tracking-[0.08em] whitespace-nowrap text-emerald-700 ring-1 ring-emerald-300">
                  {license.plan || "pro"}
                </span>
              )}
              {!demoMode && trialDaysLeft != null && (
                <span data-testid="trial-badge" title={t("license.trialActive").replace("{plan}", license?.trial?.plan || "")}
                  className="inline-flex items-center gap-0.5 rounded-sm bg-indigo-100 px-1.5 py-0.5 font-mono text-[8px] font-bold uppercase tracking-[0.08em] whitespace-nowrap text-indigo-700 ring-1 ring-indigo-300">
                  {t("license.trialShort")} · {trialDaysLeft}{t("license.dayLetter")}
                </span>
              )}
              {isAdmin && license?.buildBlocked && (
                <span data-testid="nav-coverage-badge" title={t("banner.unlicensedBuild")}
                  className="rounded-sm bg-red-100 px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase tracking-[0.15em] text-red-700 ring-1 ring-red-300">
                  {t("license.blockedShort")}
                </span>
              )}
              {isAdmin && license?.buildGrace && !license?.buildBlocked && (
                <span data-testid="nav-coverage-badge" title={t("banner.buildGrace")}
                  className="rounded-sm bg-amber-100 px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase tracking-[0.15em] text-amber-700 ring-1 ring-amber-300">
                  {t("license.grace")}{graceDaysLeft != null ? ` ${graceDaysLeft}d` : ""}
                </span>
              )}
            </div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">
              {t("brand.subtitle")}
            </div>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto py-3">
          <button type="button" onClick={() => window.dispatchEvent(new CustomEvent("open-command-palette"))}
            data-testid="command-palette-trigger"
            className="mx-3 mb-2 flex w-[calc(100%-1.5rem)] items-center justify-between rounded-sm border border-zinc-200 px-3 py-2 text-left text-sm text-zinc-400 transition-colors hover:border-zinc-300 hover:text-zinc-600">
            <span className="flex items-center gap-2"><Search size={14} /> {t("cmd.search")}</span>
            <kbd className="rounded border border-zinc-200 bg-zinc-50 px-1.5 py-0.5 font-mono text-[10px] text-zinc-400">⌘K</kbd>
          </button>
          {NAV.filter(navVisible).map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} className={(s) => `${linkCls(s)} ${n.bold ? "font-bold" : ""}`} data-testid={n.testid}>
              <n.icon size={17} strokeWidth={n.bold ? 2.5 : 2} />
              {t(n.key)}
            </NavLink>
          ))}
          {(isAdmin || can("settings.manage")) && (
            <>
              <div className="px-4 pb-1 pt-5 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">
                {t("nav.administration")}
              </div>
              {ADMIN_NAV.filter((n) => (n.adminOnly ? isAdmin : !n.perm || can(n.perm))).map((n) => (
                <NavLink key={n.to} to={n.to} className={linkCls} data-testid={n.testid}>
                  <n.icon size={17} strokeWidth={2} />
                  {t(n.key)}
                </NavLink>
              ))}
            </>
          )}
          {isSuperAdmin && (
            <>
              <div className="px-4 pb-1 pt-5 font-mono text-[10px] uppercase tracking-[0.2em] text-red-400">
                {t("nav.superadmin")}
              </div>
              <NavLink to="/superadmin" className={linkCls} data-testid="nav-superadmin">
                <ShieldAlert size={17} strokeWidth={2} />
                {t("nav.superadmin")}
                {errCount > 0 && (
                  <span data-testid="nav-superadmin-errors" className="ml-auto inline-flex min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-bold text-white">
                    {errCount > 99 ? "99+" : errCount}
                  </span>
                )}
              </NavLink>
            </>
          )}
          {e2eMode?.enabled && isAdmin && (
            <>
              <div className="px-4 pb-1 pt-5 font-mono text-[10px] uppercase tracking-[0.2em] text-sky-500">
                {t("nav.testing")}
              </div>
              <NavLink to="/e2e-history" className={linkCls} data-testid="nav-e2e-history">
                <FlaskConical size={17} strokeWidth={2} />
                {t("e2e.title")}
              </NavLink>
            </>
          )}
        </nav>

        <div className="border-t border-zinc-200 p-3">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="flex h-9 w-9 items-center justify-center bg-zinc-900 text-xs font-bold text-white">
              {initials(user?.name || user?.email)}
            </div>
            <div className="min-w-0 flex-1 leading-tight">
              <div className="truncate text-sm font-semibold text-zinc-900">{user?.name}</div>
              <div className="font-mono text-[10px] uppercase tracking-wider text-primary">{user?.role}</div>
            </div>
            <button
              onClick={doLogout}
              data-testid="logout-button"
              aria-label={t("common.signOut")}
              className="p-2 text-zinc-400 transition-colors hover:bg-zinc-100 hover:text-red-600"
            >
              <LogOut size={17} />
            </button>
          </div>
          {!demoMode && license && license.edition === "community" && license.usage && (
            <div className="mb-1 rounded-sm border border-amber-200 bg-amber-50 px-2 py-1.5" data-testid="license-usage">
              <div className="flex items-center justify-between font-mono text-[10px] font-bold uppercase tracking-wider text-amber-700">
                <span>{t("license.free")}</span>
                <span>{license.usage.employees}/{license.maxEmployees}</span>
              </div>
              <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-amber-200">
                <div className="h-full bg-amber-500" style={{ width: `${Math.min(100, Math.round((license.usage.employees / Math.max(1, license.maxEmployees)) * 100))}%` }} />
              </div>
            </div>
          )}
          <div className="px-2 pt-0.5 font-mono text-[10px] tracking-wider text-zinc-400" data-testid="app-version">
            v{APP_VERSION} · © {new Date().getFullYear()} Scalio Cloud LLC
          </div>
        </div>
      </aside>

      {/* Mobile navigation drawer */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="flex w-72 flex-col p-0" data-testid="mobile-nav-drawer">
          <div className="flex h-16 items-center gap-2.5 border-b border-zinc-200 px-5">
            <img src="/logo.svg" alt="Scalio Nexus" className="h-8 w-8 shrink-0" />
            <div className="font-display text-sm font-black uppercase tracking-tight text-zinc-950">Scalio Nexus</div>
          </div>
          <nav className="flex-1 overflow-y-auto py-3">
            {NAV.filter(navVisible).map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setMobileOpen(false)} className={(s) => `${linkCls(s)} ${n.bold ? "font-bold" : ""}`} data-testid={`m-${n.testid}`}>
                <n.icon size={18} strokeWidth={n.bold ? 2.5 : 2} />{t(n.key)}
              </NavLink>
            ))}
            {(isAdmin || can("settings.manage")) && (
              <>
                <div className="px-4 pb-1 pt-5 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">{t("nav.administration")}</div>
                {ADMIN_NAV.filter((n) => (n.adminOnly ? isAdmin : !n.perm || can(n.perm))).map((n) => (
                  <NavLink key={n.to} to={n.to} onClick={() => setMobileOpen(false)} className={linkCls} data-testid={`m-${n.testid}`}>
                    <n.icon size={18} strokeWidth={2} />{t(n.key)}
                  </NavLink>
                ))}
              </>
            )}
            {isSuperAdmin && (
              <>
                <div className="px-4 pb-1 pt-5 font-mono text-[10px] uppercase tracking-[0.2em] text-red-400">{t("nav.superadmin")}</div>
                <NavLink to="/superadmin" onClick={() => setMobileOpen(false)} className={linkCls} data-testid="m-nav-superadmin">
                  <ShieldAlert size={18} strokeWidth={2} />{t("nav.superadmin")}
                  {errCount > 0 && <span className="ml-auto inline-flex min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-bold text-white">{errCount > 99 ? "99+" : errCount}</span>}
                </NavLink>
              </>
            )}
            {e2eMode?.enabled && isAdmin && (
              <>
                <div className="px-4 pb-1 pt-5 font-mono text-[10px] uppercase tracking-[0.2em] text-sky-500">{t("nav.testing")}</div>
                <NavLink to="/e2e-history" onClick={() => setMobileOpen(false)} className={linkCls} data-testid="m-nav-e2e-history">
                  <FlaskConical size={18} strokeWidth={2} />{t("e2e.title")}
                </NavLink>
              </>
            )}
          </nav>
          <div className="border-t border-zinc-200 p-3">
            <div className="flex items-center gap-3 px-2 py-2">
              <div className="flex h-9 w-9 items-center justify-center bg-zinc-900 text-xs font-bold text-white">{initials(user?.name || user?.email)}</div>
              <div className="min-w-0 flex-1 leading-tight">
                <div className="truncate text-sm font-semibold text-zinc-900">{user?.name}</div>
                <div className="font-mono text-[10px] uppercase tracking-wider text-primary">{user?.role}</div>
              </div>
              <button onClick={doLogout} data-testid="m-logout-button" aria-label={t("common.signOut")} className="rounded-sm p-2 text-zinc-400 hover:bg-zinc-100 hover:text-red-600"><LogOut size={17} /></button>
            </div>
            <div className="px-2 pt-0.5 font-mono text-[10px] tracking-wider text-zinc-400">v{APP_VERSION} · © {new Date().getFullYear()} Scalio Cloud LLC</div>
          </div>
        </SheetContent>
      </Sheet>

      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        {/* Mobile top bar (hamburger) */}
        <div className="flex h-14 shrink-0 items-center gap-3 border-b border-zinc-200 bg-white px-4 md:hidden">
          <button onClick={() => setMobileOpen(true)} data-testid="mobile-menu-button" aria-label={t("nav.openMenu")} className="-ml-2 rounded-sm p-2 text-zinc-700 hover:bg-zinc-100">
            <Menu size={22} />
          </button>
          <img src="/logo.svg" alt="Scalio Nexus" className="h-7 w-7" />
          <div className="font-display text-sm font-black uppercase tracking-tight text-zinc-950">Scalio Nexus</div>
        </div>
        <main className="flex-1 overflow-y-auto">
        {e2eMode?.enabled && isAdmin && (
          <div data-testid="e2e-testing-banner" className="flex flex-wrap items-center gap-x-2 gap-y-1 border-b-2 border-sky-400 bg-sky-100 px-6 py-2.5 text-sm font-medium text-sky-900 md:px-10">
            <FlaskConical size={16} className="shrink-0" />
            <span>{t("e2e.testingBanner").replace("{suite}", e2eMode.suite || "")}</span>
            <NavLink to="/e2e-history" data-testid="e2e-banner-link" className="font-bold underline underline-offset-2 hover:text-sky-950">
              {t("e2e.viewHistory")}
            </NavLink>
          </div>
        )}
        {wizard?.needed && isAdmin && (
          <div data-testid="setup-wizard-banner" className="flex flex-wrap items-center gap-x-2 gap-y-1 border-b-2 border-indigo-400 bg-indigo-50 px-6 py-2.5 text-sm font-medium text-indigo-900 md:px-10">
            <Sparkles size={16} className="shrink-0" />
            <span className="font-bold">{t("wizard.bannerTitle")}</span>
            <span className="text-indigo-700">{t("wizard.bannerText")}</span>
            <button onClick={() => setWizardOpen(true)} data-testid="setup-wizard-open" className="ml-auto rounded-full bg-indigo-600 px-3 py-1 text-xs font-bold text-white transition-colors hover:bg-indigo-700">
              {t("wizard.open")}
            </button>
          </div>
        )}
        {isAdmin && !wizard?.needed && saasStatus && (() => {
          // SaaS: SSO + Graph + Exchange are all REQUIRED. Self-hosted: only count the steps the admin
          // actually started (Graph once app-reg IDs exist; SSO/EXO only when enabled), so optional
          // features never nag. Ring hides once everything started is done.
          const isSaaS = saasStatus.isSaaS;
          let steps = [];
          if (isSaaS) {
            steps = [
              !!saasStatus.enabled,
              !!(saasStatus.graphConsented && !saasStatus.graphConsentStale),
              exoRoleReady === true,
            ];
          } else {
            if (saasStatus.entraConfigured) steps.push(!!saasStatus.graphReady);
            if (saasStatus.ssoEnabledFlag) steps.push(!!saasStatus.enabled);
            if (saasStatus.exoEnabledFlag) steps.push(exoRoleReady === true);
          }
          if (steps.length === 0) return null;
          const done = steps.filter(Boolean).length;
          if (done >= steps.length) return null;
          const R = 9, C = 2 * Math.PI * R;
          const hint = isSaaS && exoRoleReady !== true ? t("wizard.progressExoPending") : t("wizard.progressHint");
          return (
            <div data-testid="setup-progress-bar" className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-indigo-200 bg-indigo-50/70 px-6 py-2 text-sm text-indigo-900 md:px-10">
              <svg width="26" height="26" viewBox="0 0 26 26" data-testid="setup-progress-ring" title={`${done} / ${steps.length}`}>
                <circle cx="13" cy="13" r={R} fill="none" stroke="#c7d2fe" strokeWidth="3" />
                <circle cx="13" cy="13" r={R} fill="none" stroke="#4f46e5" strokeWidth="3" strokeLinecap="round"
                  strokeDasharray={C} strokeDashoffset={C * (1 - done / steps.length)} transform="rotate(-90 13 13)" />
              </svg>
              <span className="font-bold" data-testid="setup-progress-count">
                {t("wizard.progressCount").replace("{done}", done).replace("{total}", steps.length)}
              </span>
              <span className="text-indigo-700">{hint}</span>
              <button onClick={() => setWizardOpen(true)} data-testid="setup-progress-open" className="ml-auto rounded-full bg-indigo-600 px-3 py-1 text-xs font-bold text-white transition-colors hover:bg-indigo-700">
                {t("wizard.open")}
              </button>
            </div>
          );
        })()}
        {isAdmin && saasStatus?.certExpiringSoon && (
          <div data-testid="cert-expiry-banner" className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-amber-300 bg-amber-50 px-6 py-2 text-sm text-amber-900 md:px-10">
            <AlertTriangle size={16} className="shrink-0 text-amber-600" />
            <span className="font-medium">
              {(saasStatus.certDaysLeft <= 0 ? t("shg.certExpired") : t("shg.certExpiring")).replace("{days}", Math.max(0, saasStatus.certDaysLeft ?? 0))}
            </span>
            <button onClick={regenerateCert} disabled={certBusy} data-testid="cert-regenerate-btn"
              className="ml-auto rounded-full bg-amber-600 px-3 py-1 text-xs font-bold text-white transition-colors hover:bg-amber-700 disabled:opacity-60">
              {certBusy ? t("shg.regenerating") : t("shg.regenCert")}
            </button>
          </div>
        )}
        {license?.buildBlocked && (
          <div data-testid="unlicensed-build-banner" className="flex items-center gap-2 border-b-2 border-yellow-500 bg-yellow-300 px-6 py-3 text-sm font-bold text-yellow-900 md:px-10">
            <AlertTriangle size={18} className="shrink-0" /> {t("banner.unlicensedBuild")}
          </div>
        )}
        {license?.buildGrace && !license?.buildBlocked && (
          <div data-testid="build-grace-banner" className="flex items-center gap-2 border-b border-amber-300 bg-amber-100 px-6 py-2 text-sm font-medium text-amber-800 md:px-10">
            <AlertTriangle size={15} className="shrink-0" />
            <span>{t("banner.buildGrace")}{graceDaysLeft != null && (
              <strong data-testid="grace-countdown" className="ml-1 font-bold">
                {" "}{t("banner.graceCountdown").replace("{n}", graceDaysLeft)}
              </strong>
            )}</span>
          </div>
        )}
        {accessMode === "readonly" && !isSuperAdmin && (
          <div data-testid="readonly-banner" className="flex items-center gap-2 border-b border-amber-300 bg-amber-100 px-6 py-2 text-sm font-medium text-amber-800 md:px-10">
            <Eye size={15} /> {accessMessage || t("banner.readonly")}
          </div>
        )}
        {children}
      </main>
      </div>
      <SetupWizard open={wizardOpen} onOpenChange={(v) => { setWizardOpen(v); if (!v) setWizardStep(null); }} status={wizard} initialStep={wizardStep} onComplete={refreshWizard} />
      <FirstLoginTour open={tourOpen} onClose={closeTour} />
    </div>
  );
}

export function PageHeader({ title, subtitle, children }) {
  return (
    <div className="sticky top-0 z-10 flex flex-wrap items-end justify-between gap-4 border-b border-zinc-200 bg-white/90 px-6 py-5 backdrop-blur md:px-10">
      <div>
        <h1 className="font-display text-3xl font-black uppercase tracking-tighter text-zinc-950">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-zinc-500">{subtitle}</p>}
      </div>
      <div className="flex items-center gap-2">{children}</div>
    </div>
  );
}
